import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import { EditOutlined, KeyOutlined, PlusOutlined, PoweroffOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

const AdminList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetForm] = Form.useForm();

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAll();
      if (response.data.success) {
        setAdmins(response.data.data || []);
      } else {
        message.error(response.data.message || '获取学校管理员列表失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '获取学校管理员列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleStatusToggle = async (id, currentStatus) => {
    const nextStatus = currentStatus === 'active' ? 'disabled' : 'active';
    try {
      await adminAPI.toggleStatus(id, nextStatus);
      message.success(nextStatus === 'active' ? '学校管理员已启用' : '学校管理员已停用');
      fetchAdmins();
    } catch (error) {
      message.error(error.response?.data?.message || '更新状态失败');
    }
  };

  const openResetPassword = (record) => {
    setResetTarget(record);
    resetForm.resetFields();
  };

  const submitResetPassword = async () => {
    const values = await resetForm.validateFields();
    setResetLoading(true);
    try {
      await adminAPI.resetPassword(resetTarget.id, values.password);
      message.success('登录密码已重置');
      setResetTarget(null);
    } catch (error) {
      message.error(error.response?.data?.message || '重置密码失败');
    } finally {
      setResetLoading(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 90 },
    { title: '登录用户名', dataIndex: 'username', key: 'username' },
    { title: '姓名', dataIndex: 'name', key: 'name', render: (value, row) => value || row.displayName || row.display_name || '-' },
    { title: '手机号', dataIndex: 'phone', key: 'phone', render: (value) => value || '-' },
    { title: '邮箱', dataIndex: 'email', key: 'email', render: (value) => value || '-' },
    { title: '所属学校', dataIndex: 'school_name', key: 'school_name', render: (value, row) => value || row.schoolName || '-' },
    {
      title: '账号状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={status === 'active' ? 'green' : 'red'}>{status === 'active' ? '正常' : '已停用'}</Tag>
    },
    {
      title: '操作',
      key: 'action',
      width: 300,
      render: (_, record) => {
        const canOperate = record.id !== user?.id;
        if (!canOperate) return <Text type="secondary">当前账号</Text>;
        return (
          <Space wrap>
            <Button size="small" onClick={() => navigate(`/schools/${record.schoolId || record.school_id}?tab=administrators`)}>管理员设置</Button>
            <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => navigate(`/admins/${record.id}/edit`)}>
              编辑
            </Button>
            <Button size="small" icon={<KeyOutlined />} onClick={() => openResetPassword(record)}>
              重置密码
            </Button>
            <Popconfirm
              title={record.status === 'active' ? '确认停用该账号的所有身份？' : '确认启用该学校管理员？'}
              onConfirm={() => handleStatusToggle(record.id, record.status)}
            >
              <Button size="small" danger={record.status === 'active'} icon={<PoweroffOutlined />}>
                {record.status === 'active' ? '停用整个账号' : '启用账号'}
              </Button>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  return (
    <>
      <Card
        title={
          <Space direction="vertical" size={0}>
            <Title level={4} style={{ margin: 0 }}>学校管理员管理</Title>
            <Text type="secondary">由平台管理员维护各学校的管理账号、登录密码和账号状态。</Text>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchAdmins} loading={loading}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/schools')}>选择学校开通管理员</Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={admins}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 10,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
        />
      </Card>

      <Modal
        open={!!resetTarget}
        title={`重置密码：${resetTarget?.name || resetTarget?.displayName || resetTarget?.username || ''}`}
        onCancel={() => setResetTarget(null)}
        onOk={submitResetPassword}
        okText="确认重置"
        confirmLoading={resetLoading}
      >
        <Form form={resetForm} layout="vertical">
          <Form.Item
            name="password"
            label="新登录密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少 6 位' }
            ]}
          >
            <Input.Password placeholder="请输入新登录密码" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AdminList;
